import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TEAM_FRAGMENT } from 'src/constants/fragments'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { IIndividualHost, IQuery, ITeam, IUser } from 'src/types/graphql'
import { isValidUUID } from 'src/utils/validateId'

// Non-admins: fetch all teams + hosts on mount
const ALL_TEAMS = gql`
  ${TEAM_FRAGMENT}

  query DashboardTeams($pagination: PaginationInput!) {
    teams(pagination: $pagination) {
      total
      hits {
        ...TeamFields
      }
    }
  }
`

// Admins: lazy search for hosts (server-side)
const SEARCH_HOSTS = gql`
  query SearchHosts($pagination: PaginationInput!, $searchText: String) {
    individualHosts(pagination: $pagination, searchText: $searchText) {
      total
      hits {
        id
        firstname
        lastname
        email
        activeListings
      }
    }
  }
`

// Admins: lazy search for teams
const SEARCH_TEAMS = gql`
  ${TEAM_FRAGMENT}

  query SearchTeams($pagination: PaginationInput!, $searchText: String) {
    teams(pagination: $pagination, searchText: $searchText) {
      total
      hits {
        ...TeamFields
      }
    }
  }
`

// Lightweight query for the currently selected team (validates access + provides display data)
const SELECTED_TEAM = gql`
  query SelectedTeam($teamId: String!) {
    team(teamId: $teamId) {
      id
      name
      legalName
      icon {
        url
      }
      members {
        userId
      }
      stripeAccountId
      shouldSetUpBankAccount
      fiscalCompliance {
        isComplete
      }
    }
  }
`

// Lightweight query for the currently selected host (admin only)
const SELECTED_HOST = gql`
  query SelectedHost($userId: String!) {
    user(userId: $userId) {
      id
      firstname
      lastname
      email
      profilePicture {
        url
      }
    }
  }
`

export type FilterMode = 'checkouts' | 'locations' | 'dashboard'

export type DashboardFilter = {
  teamId?: string
  userAndTeams?: boolean
  landlordId?: string
  userId?: string
}

export type UseDashboardFilterReturn = {
  selectedTeamId: string
  selectedHostId: string | null
  selectedTeam: ITeam | null
  selectedHost: Pick<
    IUser,
    'id' | 'firstname' | 'lastname' | 'email' | 'profilePicture'
  > | null
  handleTeamChange: (teamId: string) => void
  handleHostChange: (hostId: string | null) => void
  buildFilter: (mode: FilterMode) => DashboardFilter
  teams: ITeam[]
  hosts: IIndividualHost[]
  teamsLoading: boolean
  searchTeams: (query: string) => void
  searchResults: ITeam[]
  searching: boolean
  filterSummary: string
  hasActiveFilter: boolean
  isAdmin: boolean
  /** True once currentUser is loaded and selectedTeamId is stable */
  filterReady: boolean
  /** State-only default scope (spec §7) — see `computeDefaultScope`. */
  setComputedDefault: (scope: string | null) => void
}

type PaginationVars = {
  pagination: { from: number; pageSize: number }
}

type SearchVars = PaginationVars & { searchText?: string }

type HostsSearchData = {
  individualHosts: { total: number; hits: IIndividualHost[] }
}

const DEBOUNCE_MS = 300
const EMPTY_ARRAY_TEAMS: ITeam[] = []
const EMPTY_ARRAY_USER: IIndividualHost[] = []

// `-v2` (2026-07-20) deliberately abandons every previously-written cookie.
// Until now the hook AUTO-WROTE this cookie to the user's first team the
// moment teams loaded, so most stored values are a scope the user never
// chose — they pinned hosts to one team and hid their own personal listings.
// The auto-write is gone (see the comment above `selectedTeamId`), but a
// stale cookie would keep the old behavior alive forever, and an auto-set
// value is indistinguishable from a deliberate one. Renaming resets everyone
// once; anyone who genuinely wants a team scope re-picks it and it sticks.
const FILTER_COOKIE = 'spotme-dashboard-filter-v2'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

function readFilterCookie(): string | null {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${FILTER_COOKIE}=([^;]*)`)
    )
    const value = match?.[1] || null
    // Only return valid UUIDs — reject stale/invalid values
    if (value && !isValidUUID(value)) {
      writeFilterCookie(null)
      return null
    }
    return value
  } catch {
    return null
  }
}

function writeFilterCookie(teamId: string | null) {
  try {
    if (teamId && teamId !== 'all' && isValidUUID(teamId)) {
      document.cookie = `${FILTER_COOKIE}=${teamId};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`
    } else {
      document.cookie = `${FILTER_COOKIE}=;path=/;max-age=0;SameSite=Lax`
    }
  } catch {
    // cookie write failed
  }
}

/**
 * Spec §7: exactly one listed scope → that scope; otherwise 'all'. The USER
 * scope is "listed" only when it has any listing or the user has no teams.
 * NEVER written to the cookie — a computed default must stay computed (the
 * 2026-07-20 pinning incident came from persisting one).
 */
export function computeDefaultScope(input: {
  teamIds: string[]
  personalAnyListings: number
}): 'all' | 'none' | string {
  const listed = [
    ...(input.personalAnyListings > 0 || input.teamIds.length === 0
      ? ['none']
      : []),
    ...input.teamIds
  ]
  return listed.length === 1 ? listed[0] : 'all'
}

export default function useDashboardFilter(): UseDashboardFilterReturn {
  const router = useRouter()
  const { isAuthenticated, currentUser } = useAuthProvider()
  const logger = useLogger()
  const isAdmin = currentUser?.isAdmin ?? false

  // --- URL state ---
  // Use state (not just memo) so clearing the cookie reflects immediately
  const { teamId: teamIdParam, hostId: hostIdParam } = router.query
  const [cookieTeamId, setCookieTeamId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? readFilterCookie() : null
  )
  // Priority: URL param → cookie (non-admin) → computed default → 'all'. The
  // computed default is set by DashboardContextProvider from the server's
  // scopes (computeDefaultScope) and lives in state only — never the cookie.
  const [computedDefault, setComputedDefault] = useState<string | null>(null)
  const selectedTeamId = useMemo(() => {
    if (teamIdParam && typeof teamIdParam === 'string') return teamIdParam
    if (!isAdmin && cookieTeamId) return cookieTeamId
    if (!isAdmin && computedDefault) return computedDefault
    return 'all'
  }, [teamIdParam, isAdmin, cookieTeamId, computedDefault])
  const selectedHostId =
    hostIdParam && typeof hostIdParam === 'string' ? hostIdParam : null

  // --- Non-admin: fetch all teams on mount ---
  const [
    getAllTeams,
    { data: allTeamsData, error: allTeamsError, loading: allTeamsLoading }
  ] = useLazyQuery<Pick<IQuery, 'teams'>, PaginationVars>(ALL_TEAMS)

  useEffect(() => {
    if (allTeamsError) {
      logger.error('Error retrieving teams', allTeamsError)
    }
  }, [allTeamsError, logger])

  // Wait for currentUser: before it resolves `isAdmin` reads false, and this
  // fired the 200-team query (each team resolving its members) for every
  // admin dashboard load — ~200 DynamoDB reads that were then thrown away.
  useEffect(() => {
    if (!isAuthenticated || !currentUser || isAdmin) return
    getAllTeams({ variables: { pagination: { from: 0, pageSize: 200 } } })
  }, [isAuthenticated, currentUser, isAdmin, getAllTeams])

  // True loading state: either we don't know the user yet, or the teams query is in flight
  const teamsLoading = allTeamsLoading || (isAuthenticated && !currentUser)

  // NOTE: do NOT auto-write the filter cookie to the user's first team here.
  // That is what this block used to do, and it silently pinned every host who
  // belonged to a team to a scope they never chose. Because the team scope is
  // `teamId = X` with no union to `submittedBy`, it also hid their own
  // personal (team-less) listings from every tab — a host reported his only
  // active listing as missing on 2026-07-20. The cookie now persists ONLY an
  // explicit pick from `handleTeamChange`; absent one, `selectedTeamId`
  // defaults to 'all', which means "everything I own" (personal + all teams).

  // Non-admin teams from bulk query; admin teams are empty (search only)
  const teams: ITeam[] = isAdmin
    ? EMPTY_ARRAY_TEAMS
    : ((allTeamsData?.teams?.hits ?? EMPTY_ARRAY_TEAMS) as ITeam[])

  // --- Selected entity queries (validates access + provides display data) ---
  // No onError/onCompleted — side effects in those callbacks cause infinite loops
  // with router.replace. Instead, handle in the fetch effect via returned promise.
  const failedIdsRef = useRef<Set<string>>(new Set())

  const [fetchSelectedTeam, { data: selectedTeamData }] = useLazyQuery<
    Pick<IQuery, 'team'>,
    { teamId: string }
  >(SELECTED_TEAM)

  type SelectedHostData = {
    user: Pick<
      IUser,
      'id' | 'firstname' | 'lastname' | 'email' | 'profilePicture'
    >
  }
  const [fetchSelectedHost, { data: selectedHostData }] = useLazyQuery<
    SelectedHostData,
    { userId: string }
  >(SELECTED_HOST)

  // Fetch selected team when URL params change — validate + check access
  const isTeamSelected =
    selectedTeamId !== 'all' &&
    selectedTeamId !== 'none' &&
    isValidUUID(selectedTeamId)

  useEffect(() => {
    if (!isAuthenticated || !isTeamSelected) return
    if (failedIdsRef.current.has(selectedTeamId)) return

    fetchSelectedTeam({ variables: { teamId: selectedTeamId } })
      .then(({ data, error }) => {
        if (error) {
          logger.warn('Cannot access selected team, clearing filter', { error })
          failedIdsRef.current.add(selectedTeamId)
          writeFilterCookie(null)
          setCookieTeamId(null)
          return
        }
        // Non-admins: if members is stripped (null/empty), user is not a member
        if (
          data &&
          !isAdmin &&
          (!data.team.members || data.team.members.length === 0)
        ) {
          logger.warn(
            'User is not a member of selected team, clearing filter',
            { teamId: data.team.id }
          )
          failedIdsRef.current.add(selectedTeamId)
          writeFilterCookie(null)
          setCookieTeamId(null)
        }
      })
      .catch(() => {
        failedIdsRef.current.add(selectedTeamId)
        writeFilterCookie(null)
        setCookieTeamId(null)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isTeamSelected, selectedTeamId])

  useEffect(() => {
    if (!isAuthenticated || !selectedHostId) return
    if (failedIdsRef.current.has(selectedHostId)) return

    fetchSelectedHost({ variables: { userId: selectedHostId } }).catch(() => {
      failedIdsRef.current.add(selectedHostId)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedHostId])

  const selectedTeam: ITeam | null =
    selectedTeamId && !['all', 'none'].includes(selectedTeamId)
      ? (selectedTeamData?.team ?? null)
      : null

  const selectedHost: UseDashboardFilterReturn['selectedHost'] = selectedHostId
    ? (selectedHostData?.user ?? null)
    : null

  // --- Admin lazy search (teams + hosts) ---
  const [searchResults, setSearchResults] = useState<ITeam[]>([])
  const [hostSearchResults, setHostSearchResults] =
    useState<IIndividualHost[]>(EMPTY_ARRAY_USER)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [
    searchTeamsQuery,
    { data: searchTeamsData, error: searchTeamsError, loading: searchingTeams }
  ] = useLazyQuery<Pick<IQuery, 'teams'>, SearchVars>(SEARCH_TEAMS, {
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (searchTeamsData?.teams?.hits) {
      setSearchResults(searchTeamsData.teams.hits as ITeam[])
    }
  }, [searchTeamsData])

  useEffect(() => {
    if (searchTeamsError) {
      logger.error('Error searching teams', searchTeamsError)
    }
  }, [searchTeamsError, logger])

  const [
    searchHostsQuery,
    { data: searchHostsData, error: searchHostsError, loading: searchingHosts }
  ] = useLazyQuery<HostsSearchData, SearchVars>(SEARCH_HOSTS, {
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (searchHostsData?.individualHosts?.hits) {
      setHostSearchResults(
        searchHostsData.individualHosts.hits as IIndividualHost[]
      )
    }
  }, [searchHostsData])

  useEffect(() => {
    if (searchHostsError) {
      logger.error('Error searching hosts', searchHostsError)
    }
  }, [searchHostsError, logger])

  const searching = searchingTeams || searchingHosts

  // Hosts: admin sees search results; non-admin never sees hosts
  const hosts = isAdmin ? hostSearchResults : EMPTY_ARRAY_USER

  const searchTeams = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (!query.trim()) {
        setSearchResults([])
        setHostSearchResults(EMPTY_ARRAY_USER)
        return
      }
      debounceRef.current = setTimeout(() => {
        const vars = {
          pagination: { from: 0, pageSize: 20 },
          searchText: query.trim()
        }
        searchTeamsQuery({ variables: vars })
        searchHostsQuery({ variables: vars })
      }, DEBOUNCE_MS)
    },
    [searchTeamsQuery, searchHostsQuery]
  )

  // --- URL navigation ---
  const handleTeamChange = useCallback(
    (teamId: string) => {
      writeFilterCookie(teamId)
      setCookieTeamId(teamId === 'all' ? null : teamId)
      const query: Record<string, string | string[]> = {
        ...router.query,
        teamId
      }
      delete query.hostId
      router.push({ query }, undefined, { shallow: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.pathname]
  )

  const handleHostChange = useCallback(
    (hostId: string | null) => {
      const query = { ...router.query }
      if (hostId) {
        query.hostId = hostId
        delete query.teamId
      } else {
        query.teamId = 'all'
        delete query.hostId
      }
      router.push({ query }, undefined, { shallow: true })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.pathname]
  )

  // --- Build filter ---
  const buildFilter = useCallback(
    (mode: FilterMode): DashboardFilter => {
      if (selectedHostId) {
        const hostFilter: DashboardFilter = { userAndTeams: false }
        if (mode === 'checkouts') hostFilter.landlordId = selectedHostId
        else if (mode === 'locations') hostFilter.userId = selectedHostId
        return hostFilter
      }

      const teamId = ['all', 'none'].includes(selectedTeamId)
        ? undefined
        : selectedTeamId
      const userAndTeams =
        selectedTeamId === 'all'
          ? true
          : selectedTeamId === 'none'
            ? false
            : undefined

      return { teamId, userAndTeams }
    },
    [selectedTeamId, selectedHostId]
  )

  // --- Display helpers ---
  const filterSummary = useMemo(() => {
    if (selectedHostId) {
      if (selectedHost) {
        return (
          [selectedHost.firstname, selectedHost.lastname]
            .filter(Boolean)
            .join(' ') ||
          selectedHost.email ||
          'Anfitrión'
        )
      }
      return 'Anfitrión'
    }
    if (selectedTeamId === 'none') return 'Personal'
    if (selectedTeamId !== 'all') {
      return selectedTeam?.name ?? 'Empresa'
    }
    return 'Todas las cuentas'
  }, [selectedTeamId, selectedHostId, selectedTeam, selectedHost])

  const hasActiveFilter = selectedTeamId !== 'all' || !!selectedHostId

  // Filter is "ready" once we know the user's role (admin vs non-admin).
  // Before this, selectedTeamId may flip from cookie value to "all" when
  // isAdmin switches from false→true, causing a double-fetch.
  const filterReady = isAuthenticated && !!currentUser

  return {
    selectedTeamId,
    selectedHostId,
    selectedTeam,
    selectedHost,
    handleTeamChange,
    handleHostChange,
    buildFilter,
    teams,
    hosts,
    teamsLoading,
    searchTeams,
    searchResults,
    searching,
    filterSummary,
    hasActiveFilter,
    isAdmin,
    filterReady,
    setComputedDefault
  }
}
